import React, { useState, useEffect } from "react";
import './styles.css';
import { useTranslation } from "react-i18next";
import { Button, Col, Form, Input, Row, Upload, message } from "antd";
import ImgCrop from 'antd-img-crop';
import { useAppSelector } from "../../store";
import axios from "../../config/axios";
import { Notification } from "../../utils/notification";
import { FaCloudUploadAlt, FaRegSave } from "react-icons/fa";
import { BiSolidPencil } from "react-icons/bi";
import { FILE_URL } from "../../config/utils";
import { FaCircleUser } from "react-icons/fa6";
import { useDispatch } from "react-redux";
import { login } from "../../store/user_slice";
import useGetOneData from "../../hooks/useGetOneData";

type FieldType = {
    firstName?: string;
    lastName?: string;
    midlName?: string;
    phone?: string;
    address?: string;
    email?: string;
    avatar?: any;
    birthday?: string;
    gender?: number;
    country?: string;
    region?: string;
    remember?: boolean;
};

const MyInformation: React.FC = () => {
    const { t } = useTranslation();
    const [isActive, setisActive] = useState<boolean>(true);
    const [avatarBase64, setAvatarBase64] = useState<string | undefined>(undefined);
    const [fileList, setFileList] = useState<any[]>([]);
    const about_me = useAppSelector((state) => state?.about_me);
    const dispatch = useDispatch()

    const { data, refetch } = useGetOneData<any>({
        queryKey: ["people",],
        url: `people/${about_me?.about_me?.id}`,
        options: {
            enabled: !!about_me?.about_me?.id
        }
    });


    const [form] = Form.useForm<FieldType>();

    useEffect(() => {
        if (data) {
            form.setFieldsValue({
                firstName: data && data?.firstName,
                lastName: data && data?.lastName,
                midlName: data && data?.midlName,
                phone: data && data?.phone,
                address: data && data?.address,
                birthday: data && data?.birthday,
                avatar: `${FILE_URL}${data && data?.avatar?.contentUrl}`
            });
            setAvatarBase64(data?.avatar?.contentUrl || undefined);
        }
    }, [data, form, about_me, refetch]);

    const handleUploadChange = ({ fileList }: any) => {
        setFileList(fileList);
        if (fileList.length > 0) {
            const reader = new FileReader();
            reader.onload = (e) => setAvatarBase64(e.target?.result as string);
            reader.readAsDataURL(fileList && fileList[0].originFileObj);
        } else {
            setAvatarBase64(undefined);
        }
        const access_token = localStorage.getItem('access_token')
        if(access_token){
            dispatch(login((state:any) => state?.isLoggedIn == true))
        }
    };


    const onFinish = async (values: FieldType) => {
        try {
            const payload: any = {
                ...values,
            };

            if (avatarBase64) {
                payload.avatar = { image: avatarBase64 };
            }

            await axios.put(`/people/${about_me?.about_me?.id}`, payload);
            setisActive(!isActive);
            refetch();
            Notification('success', 'update', `${t('Information has been changed!')}`)
        } catch (error) {
            console.error("Failed to update user information", error);
            Notification('error', 'update', `${t('Error changing information!')}`)
        }
    };

    const onFinishFailed = (errorInfo: any) => {
        console.error('Failed:', errorInfo);
    };

    const uploadProps = {
        beforeUpload: (file: any) => {
            const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
            if (!isJpgOrPng) {
                message.error(`${t('You can only upload JPG/PNG file!')}`);
            }
            return isJpgOrPng || Upload.LIST_IGNORE;
        },
        onChange: handleUploadChange,
        fileList,
        maxCount: 1,
        showUploadList: false,
    };

    return (
        <div className="container mx-auto">
            <div className="grid grid-cols-12 gap-3 mt-12">
                <div className="my_profile col-span-12 border border-solid border-[#D3D3D3] rounded-xl px-6 pt-[20px] pb-4">
                    <div className="flex max-sm:flex-col sm:justify-between justify-center sm:items-end items-center max-sm:gap-2">
                        <div className="flex max-sm:flex-col max-sm:justify-center sm:items-end items-center max-sm:gap-2">
                            {isActive == false ? (
                                <ImgCrop rotationSlider>
                                    <Upload {...uploadProps}>
                                        <Button icon={<FaCloudUploadAlt />} className="w-[148px] h-[148px] rounded-[50%]">{t('Upload Avatar')}</Button>
                                    </Upload>
                                </ImgCrop>
                            ) : (
                                data && data?.avatar?.contentUrl ? <img src={`${FILE_URL}${data && data?.avatar?.contentUrl}`} alt="avatar" className="w-[148px] h-[148px] sm:mr-8 rounded-[50%]" /> : <FaCircleUser size={150} color="#29487E" className="mr-5" />
                            )}
                            <div className="max-sm:text-center">
                                <h3 className="text-xl font-bold">{data && data?.firstName} {data && data?.lastName}</h3>
                                <p className="font-semibold">{t('User')}</p>
                            </div>
                        </div>
                        <Button 
                            className="rounded-xl bg-main_color text-white text-center flex items-center px-8 font-semibold" 
                            onClick={() => setisActive(!isActive)}
                        >
                            <BiSolidPencil className="mr-2" />
                            {t('Edit')}
                        </Button>
                    </div>
                </div>
                <div className="col-span-12 border border-solid border-[#D3D3D3] rounded-xl p-4 mt-6">
                    <h3 className="text-lg font-semibold">{t('User profile edit')}</h3>
                    <Form
                        form={form}
                        name="basic"
                        initialValues={{ remember: true }}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        layout="vertical"
                        className="mt-6"
                    >
                        <Row gutter={[24, 24]}>
                            <Col span={24} sm={8} className="sm:border-r-2 sm:border-dashed">
                                <Form.Item name='firstName' label={`${t('Firstname')}`}>
                                    <Input placeholder={`${t("Firstname")}`} disabled={isActive} />
                                </Form.Item>
                                <Form.Item name='birthday' label={`${t('Birthday')}`}>
                                    <Input placeholder={`${t("Birthday")}`} disabled={isActive} />
                                </Form.Item>
                            </Col>
                            <Col span={24} sm={8} className="sm:border-r-2 sm:border-dashed">
                                <Form.Item name='lastName' label={`${t('Surname')}`}>
                                    <Input placeholder={`${t('Surname')}`} disabled={isActive} />
                                </Form.Item>
                                <Form.Item name='phone' label={t('Phone')}>
                                    <Input placeholder={`${t('Phone')}`} disabled={isActive} />
                                </Form.Item>
                            </Col>
                            <Col span={24} sm={8}>
                                <Form.Item name='midlName' label={`${t('Father name')}`}>
                                    <Input placeholder={`${t('Father name')}`} disabled={isActive} />
                                </Form.Item>
                                <Form.Item name='address' label={t('Address')}>
                                    <Input placeholder={`${t('Address')}`} disabled={isActive} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Button 
                            className="flex items-center ml-auto bg-main_color text-white px-8" 
                            htmlType="submit" 
                            disabled={isActive}
                        >
                            <FaRegSave size={18} />
                            <span className="ml-3">{t('Save')}</span>
                        </Button>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default MyInformation;

